import { useMemo, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import PreviewIcon from "../../../assets/images/Preview.png";
import PrintPreview from "../../PrintPreview/PrintPreview";
import Loader from "../../Loader/Loader";
import DynamicForm from "../../DynamicForm/DynamicForm";
import { CloseIcon } from "../../../assets/icons";
import { CloseButton } from "./SelectTemplateStyled";
import { preprocessFormFields } from "../../../utils/formGrouping";
import PoaSignatureSelect from "../../PoaSignatureSelect/PoaSignatureSelect";
const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font-family: "Manrope", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 100%;
  color: #0c53bb;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  &:hover {
    text-decoration: underline;
  }
  img {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const Overlay = styled.div`
  position: fixed;
  z-index: 1400;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(33, 41, 52, 0.32);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 800px;
  max-width: 95vw;
  box-shadow: 0 8px 32px rgba(33, 41, 52, 0.16);
  position: relative;
  font-family: "Manrope", sans-serif;
  @media (max-width: 900px) {
    width: 98vw;
    padding: 24px 8px 0 8px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  font-size: 20px;
  color: #1b2028;
  padding: 16px;
`;

const FormGrid = styled.div`
  padding: 0 20px;
  max-height: 60vh;
  overflow-y: auto;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 20px;
    max-height: 55vh;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 32px 0;
  border-top: 1px solid #e3e6e8;
  padding: 16px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const Btn = styled.button`
  font-weight: 600;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dde2e9;
  background: #f3f4f7;
  color: #1b2028;
  padding: 6px 12px;
  min-width: 68px;
  height: 32px;
  cursor: pointer;
`;

const BtnPrimary = styled(Btn)`
  background: #0c53bb;
  color: #fff;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  &:hover {
    background: #084080;
  }
`;

export default function FillFormFieldsModal({
  open,
  onClose,
  formValue = [],
  onBack,
  onGenerate,
  setValues,
  onPreview,
  templatePreview,
  setOpenPreview,
  handleShowConfirmModal,
  openPreview = false,
  matterId = null,
  poaAttorney = "",
  onPoaChange = null,
}) {
  const [isLoading, setisLoading] = useState(false);

  const processedFields = useMemo(
    () => preprocessFormFields(formValue),
    [formValue]
  );

  const handleChange = (key, newValue) => {
    setValues((prev) =>
      prev.map((field) =>
        field.key === key
          ? {
              ...field,
              value: newValue,
            }
          : field
      )
    );
  };

  const handlePreview = async () => {
    setisLoading(true);
    try {
      await onPreview();
    } finally {
      setisLoading(false);
    }
  };

  const handleDisabled = () => {
    return formValue.every((field) => {
      const val = field.value;
      return (
        (Array.isArray(val) && val.length === 0) ||
        (typeof val === "string" && !val.trim()) ||
        val === null ||
        val === undefined
      );
    });
  };

  if (!open) return null;

  return (
    <Overlay>
      <Modal>
        <Header>
          Fill Form Fields
          <CloseButton onClick={handleShowConfirmModal}>
            <CloseIcon />
          </CloseButton>
        </Header>
        {isLoading && <Loader />}
        <FormGrid>
          <DynamicForm
            formValues={processedFields}
            handleChange={handleChange}
          />

          {openPreview && (
            <PrintPreview
              printData={templatePreview}
              setOpenPreview={setOpenPreview}
            />
          )}
        </FormGrid>
        {onPoaChange && matterId && (
          <div style={{ margin: "12px 0 0", padding: "0 20px" }}>
            <label
              htmlFor="single-merge-poa"
              style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: "#374151" }}
            >
              POA signature
            </label>
            <PoaSignatureSelect
              labelId="single-merge-poa"
              matterId={matterId}
              value={poaAttorney}
              onChange={onPoaChange}
            />
          </div>
        )}
        <Actions>
          <PreviewButton
            type="button"
            disabled={handleDisabled()}
            onClick={handlePreview}
          >
            Preview <img src={PreviewIcon} alt="Preview" />
          </PreviewButton>
          <ButtonWrapper>
            <Btn onClick={onBack}>Back</Btn>
            <BtnPrimary onClick={onGenerate} disabled={handleDisabled()}>
              Generate File
            </BtnPrimary>
          </ButtonWrapper>
        </Actions>
      </Modal>
    </Overlay>
  );
}

FillFormFieldsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formValue: PropTypes.array,
  onBack: PropTypes.func,
  onGenerate: PropTypes.func,
  setValues: PropTypes.func,
  onPreview: PropTypes.func,
  templatePreview: PropTypes.object,
  setOpenPreview: PropTypes.func,
  handleShowConfirmModal: PropTypes.func,
  openPreview: PropTypes.bool,
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  poaAttorney: PropTypes.string,
  onPoaChange: PropTypes.func,
};
